import type { ApiClient } from '@carbonwebmail/api-client'
import type {
  BulkEditRequest,
  ContactFieldDto,
  ContactSortDto,
  ContactSummaryDto,
  ContactViewDto,
  SegmentDto,
} from '@carbonwebmail/contracts'
import { useState } from 'react'
import { Link, useOutletContext } from 'react-router'
import { AddColumn } from '../../ui/AddColumn.js'
import { Alert } from '../../ui/Alert.js'
import { Async } from '../../ui/Async.js'
import { Checkbox } from '../../ui/Checkbox.js'
import { ColumnDialog } from '../../ui/ColumnDialog.js'
import { ColumnMenu } from '../../ui/ColumnMenu.js'
import { DeleteColumnDialog } from '../../ui/DeleteColumnDialog.js'
import { count } from '../../ui/format.js'
import { MoreWhenSeen } from '../../ui/MoreWhenSeen.js'
import { TableSkeleton } from '../../ui/Pager.js'
import { StatusPill } from '../../ui/StatusPill.js'
import { Table, Td, Tr, TrEmpty } from '../../ui/Table.js'
import { everyRowTicked } from '../../ui/useSelection.js'
import { useMay } from '../../ui/yourRole.js'
import { REASONS } from '../deliverability/statusLabels.js'
import { useSegmentList } from '../segments/useSegmentList.js'
import { BoardToolbar } from './BoardToolbar.js'
import { BulkEdit } from './BulkEdit.js'
import type { BoardColumn } from './boardColumns.js'
import { sortedBy, valueIn } from './boardColumns.js'
import { DuplicatesDialog } from './DuplicatesDialog.js'
import { EditContactDialog } from './EditContactDialog.js'
import { FixNamesDialog } from './FixNamesDialog.js'
import { NewContactDialog } from './NewContactDialog.js'
import { SaveViewDialog } from './SaveViewDialog.js'
import { SegmentCell } from './SegmentCell.js'
import { fieldFor, useBoardColumns } from './useBoardColumns.js'
import { useContacts } from './useContacts.js'
import type { useContactViews } from './useContactViews.js'

/**
 * The contacts list.
 *
 * The column that justifies the screen is "Receiving". A contact can be
 * `subscribed` and still never receive anything, because a hard bounce or a
 * complaint sits against their address — and a list that showed only the
 * subscription status would leave someone wondering why a campaign reached
 * fewer people than the audience count promised.
 *
 * The rows only. The board above owns the heading and the tabs, and the tab
 * somebody chose reaches this through the url — see `useContacts`.
 *
 * **Ticking rows is bounded to the page**, and selecting everybody a filter
 * matches is a separate thing that says so in those words. The two are not
 * offered the same actions: connecting a segment takes either, because the
 * filter travels and the server resolves it, while **deleting and setting a
 * column are a tick list only**. Rewriting or removing forty thousand rows the
 * server resolved after the page was drawn is not something to put behind one
 * press, and nothing on screen could honestly name who it was about.
 *
 * Deleting is possible at all only since migration 0028: `send_log` and
 * `send_queue` used to cascade from a contact, so removing one erased the
 * record of every message ever sent to them. They are `set null` now, so what
 * happened survives the person being removed — and nothing is unsuppressed,
 * because the suppression list is keyed on the address.
 */
export function ContactsList({ client }: { readonly client: ApiClient }) {
  /** A viewer reads the board and changes nothing on it. */
  const mayWrite = useMay('contacts', 'write')
  const contacts = useContacts({ client })
  /**
   * The board's views, handed down rather than loaded again here.
   *
   * Two instances of `useContactViews` meant two lists: saving reloaded this
   * screen's copy and left the tab strip — drawn from the board's — showing
   * what it had at load. The view was created and no tab appeared.
   */
  const { save: saveView } = useOutletContext<ReturnType<typeof useContactViews>>()
  /**
   * The audiences a contact can be connected to — segments, not the tabs.
   *
   * These are two different lists now and were one. The tabs above are views of
   * this board; these are what a campaign is sent to.
   */
  const segments = useSegmentList({ client })
  const connectable = segments.state.status === 'ready' ? segments.state.value.segments : []
  const board = useBoardColumns({ client })
  const { fields } = board
  const { search, state, picked, all, sort, hidden, rules, notice, problem, busy } = contacts
  /**
   * Open state rather than a url.
   *
   * A url is for something worth linking to and coming back to. These are done
   * to whatever the board is showing and then left — giving each a route is
   * what made them tabs, and what meant leaving the list to add a row to it.
   *
   * They live here rather than on the board above because this is where
   * `retry` is: the thing somebody came to do is see the table change, and a
   * dialog that had to ask a parent to ask this list to reload would be a
   * second thing deciding when that happens.
   *
   * Save-as-a-view is the exception and is `contacts.savingView`, because the
   * `+` on the tab strip belongs to the board and the board does not hold what
   * a view is made of. `useContacts` says why.
   */
  const [dialog, setDialog] = useState<'new' | 'duplicates' | 'fix-names' | undefined>(undefined)
  /**
   * The row being edited, held whole rather than as an id.
   *
   * The form opens prefilled from what the board already has, so it does not
   * refetch a contact that is on screen — and a row that changed underneath
   * somebody is a row they are about to overwrite either way.
   */
  const [editing, setEditing] = useState<ContactSummaryDto | undefined>(undefined)
  /** The column whose name or kind is being changed, and the one being deleted. */
  const [editingColumn, setEditingColumn] = useState<BoardColumn | undefined>(undefined)
  const [removing, setRemoving] = useState<BoardColumn | undefined>(undefined)

  /**
   * The open view's own order and hidden columns, where the person has not
   * said otherwise.
   *
   * The server already sorts by the view when nobody asked for a column, so
   * this is only what the header arrow and the column list read. Local wins,
   * and only for this visit: changing what you look at is not editing the view.
   */
  const shown = state.status === 'ready' ? state.value.view : undefined
  const saved = shown?.kind === 'view' ? shown : undefined
  const effectiveSort = sort ?? saved?.sort ?? undefined
  const effectiveHidden = hidden ?? new Set(saved?.hidden ?? [])

  const columns = board.columns.filter((column) => !effectiveHidden.has(column.id))

  return (
    <>
      <BoardToolbar
        mayWrite={mayWrite}
        columns={board.columns}
        fields={fields}
        hidden={effectiveHidden}
        onDuplicates={() => setDialog('duplicates')}
        onFixNames={() => setDialog('fix-names')}
        onHidden={contacts.setHidden}
        onNewContact={() => setDialog('new')}
        onNewRule={contacts.newRule}
        onRules={contacts.setRules}
        onSaveView={() => contacts.setSavingView(true)}
        onSearch={contacts.setSearch}
        onSort={contacts.setSort}
        rules={rules}
        search={search}
        shown={state.status === 'ready' ? state.value : undefined}
        sort={effectiveSort}
      />

      {dialog === 'new' ? (
        <NewContactDialog
          client={client}
          onAdded={() => void contacts.retry()}
          onClose={() => setDialog(undefined)}
          segments={connectable}
        />
      ) : null}

      {dialog === 'fix-names' ? (
        <FixNamesDialog
          client={client}
          onClose={() => setDialog(undefined)}
          onFixed={() => void contacts.retry()}
        />
      ) : null}

      {dialog === 'duplicates' ? (
        <DuplicatesDialog
          client={client}
          onCleared={() => void contacts.retry()}
          onClose={() => setDialog(undefined)}
        />
      ) : null}

      {contacts.savingView ? (
        <SaveViewDialog
          hidden={[...effectiveHidden]}
          onClose={() => contacts.setSavingView(false)}
          onSave={saveView}
          rules={rules.map((one) => one.rule)}
          sort={effectiveSort}
        />
      ) : null}

      {editingColumn === undefined ? null : (
        <ColumnDialog
          column={editingColumn}
          field={fieldFor(fields, editingColumn)}
          onClose={() => setEditingColumn(undefined)}
          onSave={(change) =>
            editingColumn.kind === 'base'
              ? board.rename(editingColumn.id, change.label)
              : board.edit(editingColumn, change)
          }
        />
      )}

      {removing === undefined ? null : (
        <DeleteColumnDialog
          column={removing}
          onClose={() => setRemoving(undefined)}
          onDelete={() => board.remove(removing)}
        />
      )}

      {editing === undefined ? null : (
        <EditContactDialog
          client={client}
          contact={editing}
          fields={fields}
          onClose={() => setEditing(undefined)}
          onSaved={() => void contacts.retry()}
        />
      )}

      {problem === undefined ? null : <Alert>{problem}</Alert>}
      {notice === undefined ? null : <Alert tone="ok">{notice}</Alert>}

      {picked.length === 0 && !all ? null : (
        <Selection
          all={all}
          busy={busy}
          matching={state.status === 'ready' ? state.value.total : 0}
          onClear={() => contacts.setAll(false)}
          onConnect={(segmentId, name) =>
            void contacts.connect(segmentId, name, true, all ? undefined : page(state, picked))
          }
          columns={board.columns}
          fields={fields}
          onDelete={() => void contacts.remove(page(state, picked))}
          onEdit={(change, column) => void contacts.edit(page(state, picked), change, column)}
          onSelectAll={() => contacts.setAll(true)}
          onSuppress={(note) => void contacts.suppress(note)}
          picked={picked.length}
          segments={connectable}
        />
      )}

      <Async state={state} skeleton={<TableSkeleton />} onRetry={() => void contacts.retry()}>
        {(page) => (
          <>
            {/*
              The table is drawn whether or not it has rows, so its headings —
              and the `⋯` on each one that renames, retypes and deletes a column
              — are reachable on a board nobody has added to yet. It used to be
              swapped for a card, which took the columns with it.
            */}
            <Table
              head={[
                {
                  key: 'tick',
                  node: (
                    <Checkbox
                      checked={everyRowTicked(
                        page.contacts.map((c) => c.email),
                        picked,
                      )}
                      indeterminate={picked.length > 0}
                      label="Select every contact on this page"
                      onChange={() => contacts.toggleAllOnPage(page.contacts.map((c) => c.email))}
                    />
                  ),
                },
                ...columns.map((column) => ({
                  key: column.id,
                  node: (
                    <Header
                      column={column}
                      kind={fieldFor(fields, column)?.type}
                      onDelete={() => setRemoving(column)}
                      onEdit={() => setEditingColumn(column)}
                      onHide={() => contacts.setHidden(new Set([...effectiveHidden, column.id]))}
                      onRename={() => setEditingColumn(column)}
                      onSort={contacts.setSort}
                      sorted={sortedBy(column, effectiveSort)}
                    />
                  ),
                })),
                {
                  key: 'add',
                  node: <AddColumn client={client} onAdded={board.reload} subject="contacts" />,
                },
              ]}
              minWidth={880}
            >
              {page.contacts.length === 0 ? (
                <TrEmpty span={columns.length + 2}>
                  <Empty
                    view={page.view}
                    filtered={search !== ''}
                    onClear={() => {
                      contacts.setSearch('')
                      contacts.clearView()
                    }}
                  />
                </TrEmpty>
              ) : (
                page.contacts.map((contact) => (
                  <Row
                    busy={busy}
                    contact={contact}
                    key={contact.contactId}
                    onConnect={(segmentId, name, connecting) =>
                      void contacts.connect(segmentId, name, connecting, [contact.contactId])
                    }
                    columns={columns}
                    onEdit={() => setEditing(contact)}
                    onTick={() => contacts.toggle(contact.email)}
                    segments={connectable}
                    ticked={picked.includes(contact.email)}
                  />
                ))
              )}
            </Table>

            {page.contacts.length === 0 ? null : (
              <>
                <MoreWhenSeen disabled={contacts.everything} onMore={contacts.more} />
                <p className="mt-3 text-center text-[13px] text-t3">
                  {contacts.everything
                    ? `All ${count(page.total)} shown.`
                    : `Showing ${count(page.contacts.length)} of ${count(page.total)} — scroll for more.`}
                </p>
              </>
            )}
          </>
        )}
      </Async>
    </>
  )
}

/**
 * What can be done to the people who are ticked.
 *
 * The note is required, and the button says how many it is about. A bulk action
 * whose button says only "Suppress" is one people press without reading the
 * count, and this one cannot be undone without leaving a second row in the
 * consent record saying it was undone.
 */
function Selection({
  picked,
  all,
  matching,
  segments,
  columns,
  fields,
  busy,
  onSuppress,
  onDelete,
  onEdit,
  onConnect,
  onSelectAll,
  onClear,
}: {
  readonly picked: number
  /** True once the selection is the filter rather than the ticks. */
  readonly all: boolean
  /** How many the filter matches, which is not how many are on screen. */
  readonly matching: number
  readonly segments: readonly SegmentDto[]
  /** Every column, including hidden ones: what is on screen is not what can be set. */
  readonly columns: readonly BoardColumn[]
  readonly fields: readonly ContactFieldDto[]
  readonly busy: boolean
  readonly onSuppress: (note: string) => void
  readonly onDelete: () => void
  readonly onEdit: (change: BulkEditRequest['change'], column: string) => void
  readonly onConnect: (segmentId: string, name: string) => void
  readonly onSelectAll: () => void
  readonly onClear: () => void
}) {
  const [note, setNote] = useState('')
  /**
   * Deleting asks, and asks in the same place rather than in a dialog.
   *
   * There is no undo and the button sits beside Suppress, which is the one it
   * is most likely to be pressed instead of — and the two do opposite things to
   * the evidence: suppressing keeps the person and stops the mail, deleting
   * removes the person and keeps only what was already sent.
   */
  const [confirming, setConfirming] = useState(false)

  return (
    <div className="mb-4 flex flex-wrap items-end gap-3 rounded-card border border-line bg-surface-2 px-4 py-3">
      <p className="font-semibold text-[13px] text-t1">
        {all ? (
          <>
            All <span className="font-num tabular-nums">{count(matching)}</span> matching this
            filter
          </>
        ) : (
          <>
            <span className="font-num tabular-nums">{count(picked)}</span> selected
          </>
        )}
      </p>

      {/*
        Selecting the filter is a different thing from selecting rows, and it
        says so. The rows are never fetched — what travels is the filter, so
        what gets acted on is what matches when the server reads it rather than
        what matched when the page was drawn.
      */}
      {all ? (
        <button
          className="font-medium text-[12px] text-t3 underline hover:text-t1"
          onClick={onClear}
          type="button"
        >
          Just the {count(picked)} ticked
        </button>
      ) : matching > picked ? (
        <button
          className="font-medium text-[12px] text-brand underline"
          onClick={onSelectAll}
          type="button"
        >
          Select all {count(matching)} matching this filter
        </button>
      ) : null}

      <ConnectToSegment busy={busy} onConnect={onConnect} segments={segments} />

      {/*
        Offered for a tick list only, exactly like Delete and for the same
        reason: rewriting a column across forty thousand people the server
        resolved after the page was drawn is not something to offer behind one
        press, and nothing on screen could honestly name who it was about.
      */}
      {all ? null : (
        <BulkEdit busy={busy} columns={columns} fields={fields} onApply={onEdit} picked={picked} />
      )}

      <div className="min-w-[16rem] flex-1">
        <label className="mb-1 block font-medium text-[12px] text-t2" htmlFor="bulk-suppress-note">
          Why
        </label>
        <input
          id="bulk-suppress-note"
          type="text"
          value={note}
          disabled={busy}
          placeholder="Asked me to stop at the conference"
          onChange={(event) => setNote(event.target.value)}
          className="w-full rounded-field border border-line bg-surface px-3 py-2 text-[13px] text-t1"
        />
      </div>

      <button
        type="button"
        disabled={busy || note.trim() === ''}
        onClick={() => {
          onSuppress(note.trim())
          setNote('')
        }}
        className="rounded-pill bg-brand px-4 py-2 font-semibold text-[13px] text-brand-contrast disabled:cursor-not-allowed disabled:opacity-60"
      >
        Suppress {count(picked)}
      </button>

      {/*
        Deleting is only offered for a tick list, never for "all matching".
        Removing forty thousand people the server resolved after the page was
        drawn is not something to offer behind one press, and nothing on screen
        could honestly name who it was about.
      */}
      {all ? null : confirming ? (
        <span className="flex items-center gap-2">
          <button
            className="rounded-pill bg-danger px-4 py-2 font-semibold text-[13px] text-brand-contrast disabled:opacity-60"
            disabled={busy}
            onClick={() => {
              setConfirming(false)
              onDelete()
            }}
            type="button"
          >
            Delete {count(picked)}
          </button>
          <button
            className="font-medium text-[12px] text-t3 hover:text-t1"
            onClick={() => setConfirming(false)}
            type="button"
          >
            Keep them
          </button>
        </span>
      ) : (
        <button
          className="rounded-pill border border-line px-4 py-2 font-semibold text-[13px] text-danger hover:bg-danger-soft"
          disabled={busy}
          onClick={() => setConfirming(true)}
          type="button"
        >
          Delete
        </button>
      )}

      <p className="w-full text-[12px] text-t3">
        {confirming
          ? 'Deleting removes these contacts and cancels anything still queued to them. What was already sent stays in the record, and any address on the suppression list stays on it.'
          : 'Suppressing means nothing will reach these addresses again. It is recorded against each of them with the sentence above, which is the only trace of whatever happened outside this product.'}
      </p>
    </div>
  )
}

function Row({
  contact,
  columns,
  segments,
  busy,
  ticked,
  onTick,
  onEdit,
  onConnect,
}: {
  readonly contact: ContactSummaryDto
  readonly columns: readonly BoardColumn[]
  readonly segments: readonly SegmentDto[]
  readonly busy: boolean
  readonly ticked: boolean
  readonly onTick: () => void
  readonly onEdit: () => void
  readonly onConnect: (segmentId: string, name: string, connecting: boolean) => void
}) {
  return (
    <Tr>
      <Td>
        <Checkbox checked={ticked} label={`Select ${contact.email}`} onChange={onTick} />
      </Td>
      {columns.map((column) => (
        <Cell
          busy={busy}
          column={column}
          contact={contact}
          key={column.id}
          onConnect={onConnect}
          segments={segments}
        />
      ))}
      {/*
        Under the +, and the reason that column is not empty any more.

        Shown on hover and whenever it has focus, so it is reachable from a
        keyboard — a control that only appears under a pointer is one nobody
        tabbing can find. `group-hover` rather than a per-row state: the row is
        the group, and a hover held in React would re-render the table on every
        mouse move across it.
      */}
      <Td>
        <button
          aria-label={`Edit ${contact.email}`}
          className="rounded-field px-2 py-1 font-medium text-[12px] text-t3 opacity-0 transition-opacity hover:bg-line-2 hover:text-t1 focus-visible:opacity-100 group-hover:opacity-100"
          onClick={onEdit}
          type="button"
        >
          Edit
        </button>
      </Td>
    </Tr>
  )
}

/**
 * One cell, by which column it is.
 *
 * A switch over the column's id rather than a table of renderers: the five
 * fixed columns each draw something different, and everything else is a
 * declared field showing its value. A field with no value shows an em dash —
 * "we never collected this" and "" read alike to a person, and an empty cell
 * reads as a bug.
 */
function Cell({
  column,
  contact,
  segments,
  busy,
  onConnect,
}: {
  readonly column: BoardColumn
  readonly contact: ContactSummaryDto
  readonly segments: readonly SegmentDto[]
  readonly busy: boolean
  readonly onConnect: (segmentId: string, name: string, connecting: boolean) => void
}) {
  switch (column.id) {
    case 'name':
      return (
        <Td strong>
          {/* The name leads somewhere, because the row cannot show why an
              address is suppressed and the detail can. */}
          <Link className="text-brand" to={`/contacts/${contact.contactId}`}>
            {contact.name === '' ? contact.email : contact.name}
          </Link>
        </Td>
      )

    case 'email':
      return <Td mono>{contact.email}</Td>

    case 'company':
      return <Td>{contact.company === '' ? '—' : contact.company}</Td>

    case 'segments':
      return (
        <Td>
          <SegmentCell
            busy={busy}
            connected={contact.segments}
            onChange={onConnect}
            segments={segments}
          />
        </Td>
      )

    case 'status':
      return (
        <Td>
          <StatusPill tone={contact.status === 'subscribed' ? 'ok' : 'neutral'}>
            {contact.status}
          </StatusPill>
        </Td>
      )

    case 'receiving':
      return (
        <Td>
          {contact.suppressedFor === undefined ? (
            <StatusPill tone="ok">yes</StatusPill>
          ) : (
            // A complaint and a hard bounce are different problems with
            // different fixes, so the reason is shown rather than a single
            // "suppressed".
            <StatusPill tone={REASONS[contact.suppressedFor].tone}>
              {REASONS[contact.suppressedFor].label}
            </StatusPill>
          )}
        </Td>
      )

    default: {
      const value = valueIn(contact, column)
      return <Td>{value === '' ? '—' : value}</Td>
    }
  }
}

/**
 * Three different nothings. A tenant with no contacts needs to be told how to
 * get some; a filter that matched nothing needs clearing; and a saved view that
 * has been deleted is neither of those — the list is not empty, the view is
 * gone, and saying "no contacts match" would send somebody looking for people
 * who are there.
 *
 * Drawn inside the table rather than instead of it, so the column headings stay
 * on screen — a board whose columns vanish with its rows cannot be given its
 * first one. `TrEmpty` is the cell it sits in.
 */
function Empty({
  view,
  filtered,
  onClear,
}: {
  readonly view: ContactViewDto
  readonly filtered: boolean
  readonly onClear: () => void
}) {
  const gone = view.kind === 'view-gone'
  const narrowed = filtered || view.kind !== 'everyone'

  return (
    <>
      <h2 className="font-semibold text-[16px] text-t1">{HEADINGS[state(gone, narrowed)]}</h2>
      <p className="mx-auto mt-2 max-w-sm text-[13px] text-t2">{BODIES[state(gone, narrowed)]}</p>
      {narrowed ? (
        <button
          type="button"
          onClick={onClear}
          className="mt-4 font-semibold text-[13px] text-brand"
        >
          Show everyone
        </button>
      ) : null}
    </>
  )
}

type EmptyKind = 'gone' | 'narrowed' | 'first-run'

function state(gone: boolean, narrowed: boolean): EmptyKind {
  if (gone) return 'gone'
  return narrowed ? 'narrowed' : 'first-run'
}

const HEADINGS: Record<EmptyKind, string> = {
  gone: 'That view no longer exists',
  narrowed: 'No contacts match',
  'first-run': 'No contacts yet',
}

const BODIES: Record<EmptyKind, string> = {
  gone: 'Somebody deleted it. Your contacts are all still here — show everyone to see them.',
  narrowed: 'Try a different name or address, or show everyone.',
  'first-run': 'Contacts are the people you send to. Import a file to get started.',
}

/**
 * Connecting a whole selection to a segment.
 *
 * A picker rather than a free text box, because a segment is a thing that
 * exists and typing a name that does not match one would silently do nothing.
 *
 * **This changes who a campaign to that segment reaches** — its audience is its
 * rules or its members. It does not override an unsubscribe or a suppression:
 * those are applied when a queue is built and read again at claim (I5).
 */
function ConnectToSegment({
  segments,
  busy,
  onConnect,
}: {
  readonly segments: readonly SegmentDto[]
  readonly busy: boolean
  readonly onConnect: (segmentId: string, name: string) => void
}) {
  if (segments.length === 0) return null

  return (
    <label className="flex flex-col gap-1">
      <span className="font-medium text-[12px] text-t2">Connect to a segment</span>
      <select
        className="rounded-field border border-line bg-surface px-3 py-2 text-[13px] text-t1 disabled:opacity-60"
        defaultValue=""
        disabled={busy}
        onChange={(event) => {
          const chosen = segments.find((one) => one.segmentId === event.target.value)
          event.target.value = ''
          if (chosen !== undefined) onConnect(chosen.segmentId, chosen.name)
        }}
      >
        <option disabled value="">
          Choose one
        </option>
        {segments.map((segment) => (
          <option key={segment.segmentId} value={segment.segmentId}>
            {segment.name}
          </option>
        ))}
      </select>
    </label>
  )
}

/** The ticked rows, as contact ids: the toolbar holds addresses, the api takes ids. */
function page(
  state: ReturnType<typeof useContacts>['state'],
  picked: readonly string[],
): readonly string[] {
  if (state.status !== 'ready') return []
  return state.value.contacts
    .filter((contact) => picked.includes(contact.email))
    .map((contact) => contact.contactId)
}

/**
 * A column heading: the control that orders by it, and the menu of everything
 * else that can be done to it.
 *
 * Pressing the name sorts ascending, and pressing the one already sorted turns
 * it round — which is what a table header does everywhere. A column that cannot
 * be ordered is a word and nothing else: a button that did nothing would be
 * worse than no button, and the `⋯` beside it still opens.
 */
function Header({
  column,
  kind,
  sorted,
  onSort,
  onHide,
  onRename,
  onEdit,
  onDelete,
}: {
  readonly column: BoardColumn
  readonly kind: ContactFieldDto['type'] | undefined
  readonly sorted: 'asc' | 'desc' | undefined
  readonly onSort: (sort: ContactSortDto | undefined) => void
  readonly onHide: () => void
  readonly onRename: () => void
  readonly onEdit: () => void
  readonly onDelete: () => void
}) {
  const next = column.sort

  return (
    <span className="group/head flex items-center justify-between gap-1">
      {next === undefined ? (
        <span>{column.label}</span>
      ) : (
        <button
          className={`flex items-center gap-1 font-semibold text-[12px] uppercase tracking-[0.04em] ${
            sorted === undefined ? 'text-t2 hover:text-t1' : 'text-brand'
          }`}
          onClick={() => onSort({ ...next, direction: sorted === 'asc' ? 'desc' : 'asc' })}
          type="button"
        >
          {column.label}
          <span aria-hidden="true">{sorted === undefined ? '' : sorted === 'asc' ? '↑' : '↓'}</span>
        </button>
      )}

      <ColumnMenu
        kind={kind}
        label={column.label}
        onDelete={column.kind === 'field' ? onDelete : undefined}
        // The name is the way back to the contact, so a board without it is not
        // a board. Every other column can go.
        onHide={column.fixed ? undefined : onHide}
        onRename={onRename}
        onRetype={column.kind === 'field' ? onEdit : undefined}
        // A contact sort names a declared field's key as well as a column, and
        // the menu speaks only in directions — so the column it belongs to is
        // put back here.
        onSort={
          next === undefined
            ? undefined
            : (direction) => onSort(direction === undefined ? undefined : { ...next, direction })
        }
        sorted={sorted}
      />
    </span>
  )
}
